import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react'
import { Bar, CartesianGrid, ComposedChart, Line, Tooltip, XAxis, YAxis } from 'recharts'
import { RechartsChartTooltipProvider, StableRechartsChartTooltip } from './ChartTooltip'
import { addTrendToData } from '../dashboard/utils/trendline'
import { formatCompactNumber } from './formatters'
import './chart.css'

const MIN_CATEGORY_GAP = 0
const MAX_CATEGORY_GAP = 40
const DEFAULT_CATEGORY_GAP = MIN_CATEGORY_GAP
const GAP_STEP = 4
const MIN_CATEGORY_WIDTH = 118
const SINGLE_Y_AXIS_PANEL_WIDTH = 56
const DUAL_Y_AXIS_PANEL_WIDTH = 112
const Y_AXIS_WIDTH = 44
const X_AXIS_HEIGHT = 80
const X_AXIS_LINE_HEIGHT = 14
const LEGEND_GAP = 32
const LEFT_Y_AXIS_LABEL = 'Impressions'
const RIGHT_Y_AXIS_LABEL = 'Engagements'

function getSeriesKeysForAxis(series, axisId) {
  return (series || [])
    .filter((item) => (item.yAxisId || 'left') === axisId)
    .map((item) => item.key)
    .filter(Boolean)
}

function getSeriesMax(data, keys) {
  if (!data?.length || !keys?.length) return 0
  return Math.max(0, ...data.flatMap((row) => keys.map((key) => Number(row[key]) || 0)))
}

function getNiceMax(value) {
  if (!Number.isFinite(value) || value <= 0) return 10
  const magnitude = 10 ** Math.floor(Math.log10(value))
  return Math.ceil(value / magnitude) * magnitude
}

function getNumericAxisLabel(value, side = 'left') {
  if (!value) return undefined
  return {
    value,
    angle: -90,
    position: side === 'right' ? 'right' : 'left',
    offset: 8,
    fill: '#6b7280',
    fontSize: 12,
    style: { textAnchor: 'middle' },
  }
}

function getCampaignHorizontalGridCoordinates({ yAxis }) {
  const coordinates = (yAxis?.ticks || [])
    .map((tick) => tick.coordinate)
    .filter((coord) => Number.isFinite(coord))

  if (coordinates.length <= 1) return coordinates

  const topCoordinate = Math.min(...coordinates)
  return coordinates.filter((coord) => coord !== topCoordinate)
}

function getCategorySlotWidth(categoryGap) {
  const gapMultiplier = 1 + categoryGap / 12
  return MIN_CATEGORY_WIDTH * gapMultiplier
}

function getChartWidth(containerWidth, categoryCount, categoryGap) {
  if (categoryCount <= 0) return containerWidth > 0 ? containerWidth : 800

  const minChartWidth = categoryCount * getCategorySlotWidth(categoryGap)
  const baseWidth = containerWidth > 0 ? containerWidth : 800

  if (minChartWidth >= baseWidth) return minChartWidth
  return baseWidth
}

function wrapLabel(str, maxCharsPerLine = 14, maxLines = 2) {
  if (!str || typeof str !== 'string') return [String(str || '')]
  const s = String(str).trim()
  if (!s) return ['']
  if (s.length <= maxCharsPerLine) return [s]

  const words = s.split(/\s+/)
  const lines = []
  let current = ''

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word
    if (next.length <= maxCharsPerLine) {
      current = next
      return
    }
    if (current) lines.push(current)
    if (word.length > maxCharsPerLine) {
      for (let i = 0; i < word.length; i += maxCharsPerLine) {
        lines.push(word.slice(i, i + maxCharsPerLine))
      }
      current = ''
      return
    }
    current = word
  })
  if (current) lines.push(current)

  if (lines.length <= maxLines) return lines
  const trimmed = lines.slice(0, maxLines)
  const last = trimmed[maxLines - 1]
  trimmed[maxLines - 1] =
    last.length > maxCharsPerLine - 1 ? `${last.slice(0, maxCharsPerLine - 1)}…` : `${last}…`
  return trimmed
}

function XAxisWrapTick({ x, y, payload, slotWidth = MIN_CATEGORY_WIDTH }) {
  const maxChars = Math.max(8, Math.min(18, Math.floor(slotWidth / 7)))
  const lines = wrapLabel(payload?.value, maxChars, 2)
  return (
    <g transform={`translate(${x},${y + 6})`}>
      <text textAnchor="middle" fontSize={12} fill="#4b5563">
        {lines.map((line, i) => (
          <tspan key={i} x={0} dy={i === 0 ? 0 : X_AXIS_LINE_HEIGHT}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  )
}

const TREND_WINDOW = 7
const EVENT_POST_TOOLTIP_WIDTH = 280
const EVENT_POST_TOOLTIP_HEIGHT = 220
const EVENT_POST_TOOLTIP_OFFSET = 12

function computeTooltipPosition(anchorX, anchorY, width, height, bounds) {
  const margin = 8
  const maxX = bounds?.width != null ? bounds.width - margin : window.innerWidth - margin
  const maxY = bounds?.height != null ? bounds.height - margin : window.innerHeight - margin

  let x = anchorX + EVENT_POST_TOOLTIP_OFFSET
  let y = anchorY - height - EVENT_POST_TOOLTIP_OFFSET

  if (y < margin) {
    y = anchorY + EVENT_POST_TOOLTIP_OFFSET
  }
  if (y + height > maxY) {
    y = Math.max(margin, maxY - height)
  }
  if (x + width > maxX) {
    x = anchorX - width - EVENT_POST_TOOLTIP_OFFSET
  }
  if (x < margin) {
    x = margin
  }

  return { x, y }
}

function getChartAnchorElement(scrollContainerRef) {
  return scrollContainerRef.current?.querySelector('.recharts-wrapper') ?? scrollContainerRef.current
}

function TooltipPortalBridge({ tooltipProps, onTooltipChange }) {
  const latestPropsRef = useRef(tooltipProps)
  latestPropsRef.current = tooltipProps

  useLayoutEffect(() => {
    if (tooltipProps.active && tooltipProps.payload?.length) {
      onTooltipChange(latestPropsRef.current)
      return
    }
    onTooltipChange(null)
  }, [
    tooltipProps.active,
    tooltipProps.label,
    tooltipProps.coordinate?.x,
    tooltipProps.coordinate?.y,
    onTooltipChange,
  ])

  useEffect(() => () => onTooltipChange(null), [onTooltipChange])

  return null
}

function PortaledChartTooltip({ tooltipProps, scrollContainerRef, tooltipLayerRef, renderContent }) {
  const tooltipRef = useRef(null)
  const [position, setPosition] = useState({ x: 0, y: 0 })

  const updatePosition = useCallback(() => {
    if (!tooltipProps?.coordinate || !tooltipLayerRef.current) return

    const anchorEl = getChartAnchorElement(scrollContainerRef)
    if (!anchorEl) return

    const anchorRect = anchorEl.getBoundingClientRect()
    const layerRect = tooltipLayerRef.current.getBoundingClientRect()
    const tooltipWidth = tooltipRef.current?.offsetWidth || EVENT_POST_TOOLTIP_WIDTH
    const tooltipHeight = tooltipRef.current?.offsetHeight || EVENT_POST_TOOLTIP_HEIGHT
    const anchorX = anchorRect.left - layerRect.left + tooltipProps.coordinate.x
    const anchorY = anchorRect.top - layerRect.top + tooltipProps.coordinate.y
    const nextPosition = computeTooltipPosition(
      anchorX,
      anchorY,
      tooltipWidth,
      tooltipHeight,
      { width: layerRect.width, height: layerRect.height }
    )
    setPosition((prev) => (
      prev.x === nextPosition.x && prev.y === nextPosition.y ? prev : nextPosition
    ))
  }, [tooltipProps, scrollContainerRef, tooltipLayerRef])

  useLayoutEffect(() => {
    updatePosition()
  }, [updatePosition])

  useEffect(() => {
    const scrollEl = scrollContainerRef.current
    const handleReposition = () => requestAnimationFrame(updatePosition)
    scrollEl?.addEventListener('scroll', handleReposition, { passive: true })
    window.addEventListener('resize', handleReposition)
    return () => {
      scrollEl?.removeEventListener('scroll', handleReposition)
      window.removeEventListener('resize', handleReposition)
    }
  }, [scrollContainerRef, updatePosition])

  useLayoutEffect(() => {
    if (!tooltipRef.current) return undefined
    const observer = new ResizeObserver(() => requestAnimationFrame(updatePosition))
    observer.observe(tooltipRef.current)
    return () => observer.disconnect()
  }, [updatePosition, tooltipProps.label])

  if (!tooltipProps?.coordinate) return null

  return (
    <div
      ref={tooltipRef}
      className="campaign-perf-tooltip-portal"
      style={{ left: position.x, top: position.y }}
    >
      {renderContent(tooltipProps)}
    </div>
  )
}

function ScrollHint({ direction, onClick, label }) {
  return (
    <button
      type="button"
      className={`scrollable-bar-chart__hint scrollable-bar-chart__hint--${direction}`}
      onClick={onClick}
      aria-label={label}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        {direction === 'right' ? (
          <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        ) : (
          <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        )}
      </svg>
    </button>
  )
}

export default function CampaignPerformanceBarChart({
  data,
  xKey,
  series,
  height = 260,
  emptyMessage = 'No data available',
  tooltipExtra,
  tooltipContent,
  labelFormatter,
  showTooltip = true,
  showTrend = true,
  forceEmpty = false,
  legend = null,
}) {
  const containerRef = useRef(null)
  const scrollContainerRef = useRef(null)
  const tooltipLayerRef = useRef(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [categoryGap, setCategoryGap] = useState(DEFAULT_CATEGORY_GAP)
  const [portalTooltipProps, setPortalTooltipProps] = useState(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const handlePortalTooltipChange = useCallback((next) => setPortalTooltipProps(next), [])

  const usesDualYAxis = (series || []).some((item) => (item.yAxisId || 'left') === 'right')
  const leftAxisPanelWidth = usesDualYAxis ? DUAL_Y_AXIS_PANEL_WIDTH : SINGLE_Y_AXIS_PANEL_WIDTH
  const rightAxisPanelWidth = usesDualYAxis ? DUAL_Y_AXIS_PANEL_WIDTH : 0
  const axisPanelGutter = leftAxisPanelWidth + rightAxisPanelWidth

  const updateScrollMetrics = useCallback(() => {
    const node = scrollContainerRef.current
    if (!node) return
    const { scrollLeft, scrollWidth, clientWidth } = node
    setCanScrollLeft(scrollLeft > 4)
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4)
  }, [])

  // Measure scroll viewport from the chart container so dual-axis gutters stay excluded.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateWidth = () => {
      const width = Math.max(0, container.clientWidth - axisPanelGutter)
      setContainerWidth(width)
      updateScrollMetrics()
    }

    const timeoutId = setTimeout(updateWidth, 0)

    const resizeObserver = new ResizeObserver(() => {
      updateWidth()
    })

    resizeObserver.observe(container)

    window.addEventListener('resize', updateWidth)
    
    return () => {
      clearTimeout(timeoutId)
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateWidth)
    }
  }, [axisPanelGutter, updateScrollMetrics])

  useEffect(() => {
    const node = scrollContainerRef.current
    if (!node) return undefined

    const runUpdate = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(updateScrollMetrics)
      })
    }

    runUpdate()
    node.addEventListener('scroll', updateScrollMetrics, { passive: true })

    return () => {
      node.removeEventListener('scroll', updateScrollMetrics)
    }
  }, [updateScrollMetrics, containerWidth, categoryGap, data])

  const empty = forceEmpty || !data || data.length === 0
  const n = data?.length || 0

  const seriesKeys = (series || []).map((s) => s.key).filter(Boolean)
  const dataWithTrend = showTrend && seriesKeys.length > 0
    ? addTrendToData(data, seriesKeys, Math.min(TREND_WINDOW, Math.max(1, data.length - 1)))
    : data

  const chartWidth = getChartWidth(containerWidth, n, categoryGap)
  const minChartWidth = n * getCategorySlotWidth(categoryGap)
  const isScrollable = minChartWidth > (containerWidth > 0 ? containerWidth + 1 : 0)

  const scrollByDirection = (direction) => {
    const node = scrollContainerRef.current
    if (!node) return
    const delta = direction === 'right' ? node.clientWidth * 0.75 : -node.clientWidth * 0.75
    node.scrollBy({ left: delta, behavior: 'smooth' })
  }

  const normalizedSeries = (series || []).map((item) => ({
    ...item,
    formatter: item.formatter || ((v) => (v != null ? String(v) : '?')),
    yAxisId: item.yAxisId || 'left',
  }))

  const trendSeries = showTrend
    ? seriesKeys.map((key) => ({
        key: `${key}Trend`,
        label: `Trend (${TREND_WINDOW}-day avg)`,
        colorVar: normalizedSeries.find((s) => s.key === key)?.colorVar || '--chart-1',
        formatter: normalizedSeries.find((s) => s.key === key)?.formatter || ((v) => (v != null ? String(v) : '?')),
        yAxisId: normalizedSeries.find((s) => s.key === key)?.yAxisId || 'left',
      }))
    : []

  const valueKeys = useMemo(
    () => [...seriesKeys, ...trendSeries.map((item) => item.key)],
    [seriesKeys, trendSeries]
  )

  const leftAxisKeys = useMemo(
    () => (usesDualYAxis ? getSeriesKeysForAxis(normalizedSeries, 'left') : seriesKeys),
    [usesDualYAxis, normalizedSeries, seriesKeys]
  )

  const rightAxisKeys = useMemo(
    () => getSeriesKeysForAxis(normalizedSeries, 'right'),
    [normalizedSeries]
  )

  const leftTrendKeys = useMemo(
    () => trendSeries.filter((item) => item.yAxisId === 'left').map((item) => item.key),
    [trendSeries]
  )

  const rightTrendKeys = useMemo(
    () => trendSeries.filter((item) => item.yAxisId === 'right').map((item) => item.key),
    [trendSeries]
  )

  const yDomainLeft = useMemo(() => {
    const keys = usesDualYAxis ? [...leftAxisKeys, ...leftTrendKeys] : valueKeys
    const max = getNiceMax(getSeriesMax(dataWithTrend, keys))
    return [0, max]
  }, [usesDualYAxis, dataWithTrend, leftAxisKeys, leftTrendKeys, valueKeys])

  const yDomainRight = useMemo(() => {
    if (!usesDualYAxis) return [0, 10]
    const max = getNiceMax(getSeriesMax(dataWithTrend, [...rightAxisKeys, ...rightTrendKeys]))
    return [0, max]
  }, [usesDualYAxis, dataWithTrend, rightAxisKeys, rightTrendKeys])

  const categorySlotWidth = n > 0 ? chartWidth / n : MIN_CATEGORY_WIDTH
  const chartMargins = {
    top: 12,
    right: usesDualYAxis ? 12 : 24,
    left: 0,
    bottom: 12,
  }
  const yAxisMargins = {
    top: 12,
    bottom: 24 + X_AXIS_HEIGHT,
    left: usesDualYAxis ? 40 : 0,
    right: 6,
  }
  const rightYAxisMargins = {
    top: 12,
    bottom: 24 + X_AXIS_HEIGHT,
    left: 6,
    right: usesDualYAxis ? 40 : 0,
  }

  const leftAxisSeries = normalizedSeries.find((item) => item.yAxisId === 'left')
  const rightAxisSeries = normalizedSeries.find((item) => item.yAxisId === 'right')

  const allSeriesForTooltip = [...normalizedSeries, ...trendSeries]
  const useCustomTooltip = typeof tooltipContent === 'function'

  const portaledTooltipContent = useMemo(() => {
    if (!useCustomTooltip) return null
    return function PortalBridge(props) {
      return <TooltipPortalBridge tooltipProps={props} onTooltipChange={handlePortalTooltipChange} />
    }
  }, [useCustomTooltip, handlePortalTooltipChange])

  if (empty) {
    return (
      <div className="rg-chart__empty campaign-perf-chart-empty" role="status">
        {emptyMessage || 'No data available'}
      </div>
    )
  }

  const handleZoomOut = () => setCategoryGap((g) => Math.max(MIN_CATEGORY_GAP, g - GAP_STEP))
  const handleZoomIn = () => setCategoryGap((g) => Math.min(MAX_CATEGORY_GAP, g + GAP_STEP))
  const handleReset = () => setCategoryGap(MIN_CATEGORY_GAP)

  const barCategoryGap = `${Math.max(0, Math.min(40, categoryGap))}%`
  const barSize = Math.min(16, 11 + Math.round(categoryGap / 6))
  const chartBodyStyle = {
    height,
    gridTemplateColumns: usesDualYAxis
      ? `${leftAxisPanelWidth}px minmax(0, 1fr) ${rightAxisPanelWidth}px`
      : `${leftAxisPanelWidth}px minmax(0, 1fr)`,
  }

  return (
    <div className="campaign-perf-chart" ref={containerRef}>
      <div
        className={`campaign-perf-chart-body scrollable-bar-chart${usesDualYAxis ? ' campaign-perf-chart-body--dual-axis' : ''}`}
        style={chartBodyStyle}
      >
        <div
          className="scrollable-bar-chart__y-axis"
          style={{ width: leftAxisPanelWidth, minWidth: leftAxisPanelWidth, height }}
        >
          <ComposedChart
            width={leftAxisPanelWidth}
            height={height}
            data={dataWithTrend}
            margin={yAxisMargins}
          >
            <YAxis
              yAxisId={usesDualYAxis ? 'left' : undefined}
              type="number"
              domain={yDomainLeft}
              width={Y_AXIS_WIDTH}
              tickCount={5}
              allowDecimals={false}
              stroke="var(--chart-muted)"
              fontSize={14}
              tickLine={false}
              axisLine={{ stroke: 'var(--chart-border)' }}
              tick={{ fill: '#374151' }}
              tickFormatter={formatCompactNumber}
              label={usesDualYAxis ? getNumericAxisLabel(LEFT_Y_AXIS_LABEL, 'left') : undefined}
            />
            <XAxis dataKey={xKey} type="category" hide height={0} axisLine={false} tickLine={false} />
            {(leftAxisSeries || normalizedSeries[0]) && (
              <Bar
                dataKey={(leftAxisSeries || normalizedSeries[0]).key}
                yAxisId={usesDualYAxis ? 'left' : undefined}
                fill="transparent"
                fillOpacity={0}
                stroke="none"
                isAnimationActive={false}
              />
            )}
          </ComposedChart>
        </div>

        <div
          className={`campaign-perf-chart-scroll-wrap scrollable-bar-chart__scroll-wrap${isScrollable ? ' campaign-perf-chart-scroll-wrap--scrollable scrollable-bar-chart__scroll-wrap--scrollable' : ''}`}
        >
          {isScrollable && canScrollLeft && (
            <ScrollHint
              direction="left"
              label="Scroll chart left"
              onClick={() => scrollByDirection('left')}
            />
          )}
          {isScrollable && canScrollRight && (
            <ScrollHint
              direction="right"
              label="Scroll chart right"
              onClick={() => scrollByDirection('right')}
            />
          )}
          <RechartsChartTooltipProvider
            series={allSeriesForTooltip}
            options={
              useCustomTooltip
                ? {}
                : {
                    labelFormatter,
                    extraContent: tooltipExtra,
                    className: 'rg-chart__tooltip--campaign',
                  }
            }
          >
            <div
              className={`campaign-perf-chart-scroll scrollable-bar-chart__scroll${isScrollable ? ' campaign-perf-chart-scroll--scrollable' : ''}`}
              ref={scrollContainerRef}
              aria-label="Chart (scroll horizontally)"
            >
              <ComposedChart
                width={chartWidth}
                height={height}
                data={dataWithTrend}
                margin={chartMargins}
                barCategoryGap={barCategoryGap}
                barGap={4}
                barSize={barSize}
              >
                <CartesianGrid
                  stroke="var(--chart-grid)"
                  strokeDasharray="3 3"
                  vertical={false}
                  yAxisId={usesDualYAxis ? 'left' : undefined}
                  horizontalCoordinatesGenerator={getCampaignHorizontalGridCoordinates}
                />
                <XAxis
                  type="category"
                  dataKey={xKey}
                  stroke="var(--chart-muted)"
                  tickLine={false}
                  axisLine={{ stroke: 'var(--chart-border)' }}
                  height={X_AXIS_HEIGHT}
                  interval={0}
                  tick={<XAxisWrapTick slotWidth={categorySlotWidth} />}
                  angle={0}
                  textAnchor="middle"
                  tickMargin={12}
                />
                {usesDualYAxis ? (
                  <>
                    <YAxis
                      yAxisId="left"
                      type="number"
                      domain={yDomainLeft}
                      hide
                      width={0}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      type="number"
                      domain={yDomainRight}
                      hide
                      width={0}
                      axisLine={false}
                      tickLine={false}
                    />
                  </>
                ) : (
                  <YAxis type="number" domain={yDomainLeft} hide width={0} axisLine={false} tickLine={false} />
                )}
                {showTooltip && (
                  <Tooltip
                    content={useCustomTooltip ? portaledTooltipContent : StableRechartsChartTooltip}
                    shared={useCustomTooltip}
                    wrapperStyle={useCustomTooltip ? { display: 'none' } : undefined}
                    cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  />
                )}
                {normalizedSeries.map((item) => (
                  <Bar
                    key={item.key}
                    dataKey={item.key}
                    yAxisId={usesDualYAxis ? item.yAxisId : undefined}
                    fill={`var(${item.colorVar})`}
                    radius={[3, 3, 0, 0]}
                    name={item.label}
                    activeBar={
                      showTooltip && useCustomTooltip
                        ? { fillOpacity: 0.92, stroke: 'var(--chart-border)', strokeWidth: 1 }
                        : undefined
                    }
                  />
                ))}
                {showTrend &&
                  trendSeries.map((item) => (
                    <Line
                      key={item.key}
                      type="monotone"
                      dataKey={item.key}
                      yAxisId={usesDualYAxis ? item.yAxisId : undefined}
                      name={item.label}
                      stroke={`var(${item.colorVar})`}
                      strokeWidth={2}
                      strokeOpacity={0.6}
                      dot={false}
                      connectNulls
                    />
                  ))}
              </ComposedChart>
            </div>
            <div ref={tooltipLayerRef} className="campaign-perf-tooltip-layer" aria-hidden={!showTooltip || !portalTooltipProps}>
              {showTooltip && useCustomTooltip && portalTooltipProps && (
                <PortaledChartTooltip
                  tooltipProps={portalTooltipProps}
                  scrollContainerRef={scrollContainerRef}
                  tooltipLayerRef={tooltipLayerRef}
                  renderContent={tooltipContent}
                />
              )}
            </div>
          </RechartsChartTooltipProvider>
        </div>

        {usesDualYAxis && (
          <div
            className="scrollable-bar-chart__y-axis scrollable-bar-chart__y-axis--right"
            style={{ width: rightAxisPanelWidth, minWidth: rightAxisPanelWidth, height }}
          >
            <ComposedChart
              width={rightAxisPanelWidth}
              height={height}
              data={dataWithTrend}
              margin={rightYAxisMargins}
            >
              <YAxis
                yAxisId="right"
                orientation="right"
                type="number"
                domain={yDomainRight}
                width={Y_AXIS_WIDTH}
                tickCount={5}
                allowDecimals={false}
                stroke="var(--chart-muted)"
                fontSize={14}
                tickLine={false}
                axisLine={{ stroke: 'var(--chart-border)' }}
                tick={{ fill: '#374151' }}
                tickFormatter={formatCompactNumber}
                label={getNumericAxisLabel(RIGHT_Y_AXIS_LABEL, 'right')}
              />
              <XAxis dataKey={xKey} type="category" hide height={0} axisLine={false} tickLine={false} />
              {rightAxisSeries && (
                <Bar
                  dataKey={rightAxisSeries.key}
                  yAxisId="right"
                  fill="transparent"
                  fillOpacity={0}
                  stroke="none"
                  isAnimationActive={false}
                />
              )}
            </ComposedChart>
          </div>
        )}
      </div>
      {legend ? (
        <div
          className="campaign-perf-chart-legend"
          style={{ marginTop: LEGEND_GAP }}
        >
          {legend}
        </div>
      ) : null}
      <div
        className="campaign-perf-zoom-overlay"
        style={usesDualYAxis ? { right: `${rightAxisPanelWidth + 8}px` } : undefined}
      >
        <button type="button" className="campaign-perf-zoom-btn" onClick={handleZoomOut} aria-label="Zoom out (decrease gap between events)">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
        <button type="button" className="campaign-perf-zoom-btn" onClick={handleZoomIn} aria-label="Zoom in (increase gap between events)">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
        <button type="button" className="campaign-perf-zoom-btn" onClick={handleReset} aria-label="Reset gap">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
          </svg>
        </button>
      </div>
    </div>
  )
}
